import { DentalOfficeShiftCard } from '@tempmee/react-blocks';
import { INTERVIEW_INVITE_SENT_COPY } from '~/components/counterJobs/OpenJobScreenRedesign/InterviewInviteSection';
import { InviteSuccessPopup } from '~/components/counterJobs/OpenJobScreenRedesign/InviteSuccessPopup';
import { CancelShiftConfirmPopup } from '~/components/shared/CancelShiftConfirmPopup';
import type { IGetOnsiteInterviewListViewEntity } from '~/domain/PermanentJobs';
import { mapOnsiteInterviewToShiftCard } from '../../mapOnsiteInterviewToShiftCard';
import { PostingInterviewInviteDialog } from './PostingInterviewInviteDialog';
import {
  type IUsePostingInterviewCardArgs,
  usePostingInterviewCard,
} from './usePostingInterviewCard';

interface IPostingInterviewCardProps extends IUsePostingInterviewCardArgs {
  officeName: string;
  /** Profession badge code, e.g. "RDH". */
  profession?: string;
  onOpen: (interview: IGetOnsiteInterviewListViewEntity) => void;
}

/**
 * One interview on Posting Details' Interviews tab: the DS shift card with the actions
 * the dashboard's shift lists give an interview, and the popups they open. Logic lives
 * in `usePostingInterviewCard`.
 */
export const PostingInterviewCard = ({
  interview,
  jobPostingId,
  officeId,
  officeName,
  profession,
  onOpen,
}: IPostingInterviewCardProps) => {
  const {
    menuItems,
    openInvite,
    inviteTarget,
    isInviteOpen,
    closeInvite,
    handleInvited,
    isInviteSentOpen,
    closeInviteSent,
    isCancelOpen,
    closeCancel,
    confirmCancel,
    isCancelPending,
  } = usePostingInterviewCard({ interview, jobPostingId, officeId });

  return (
    <>
      <DentalOfficeShiftCard
        {...mapOnsiteInterviewToShiftCard(interview, { officeName, profession })}
        onCardClick={() => onOpen(interview)}
        onMessage={openInvite}
        menuItems={menuItems}
        menuButtonTestID="posting-interview-menu"
        data-cy="onsite-interview-list-item"
      />

      {isInviteOpen && (
        <PostingInterviewInviteDialog
          target={inviteTarget}
          date={interview.localDate}
          onClose={closeInvite}
          onInvited={handleInvited}
        />
      )}

      {isInviteSentOpen && (
        <InviteSuccessPopup
          dataCy="posting-interview-invite-success"
          description={INTERVIEW_INVITE_SENT_COPY}
          onClose={closeInviteSent}
        />
      )}

      {isCancelOpen && (
        <CancelShiftConfirmPopup
          isInterview
          date={interview.localDate}
          isPending={isCancelPending}
          onClose={closeCancel}
          onConfirm={confirmCancel}
        />
      )}
    </>
  );
};
